/**
 * React Query hooks da Sub-tela 3 — IA (Fase 2 multi-modelo).
 *
 * Lê e escreve em `/v1/tenant-ai-config/*` (capability `custom-model-selection`).
 * Após cada mutation, invalida o cache local — o engine já invalida o cache
 * de `resolveEffectiveModel` server-side (Task #3).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type {
  AgentModelConfigWire,
  AiProfileWire,
  ApplyAiProfileResponse,
  DynamismScaleResponse,
  DynamismScaleWire,
  FailoverConfigWire,
  ProviderModelsWire,
  SetAgentFallbackResponse,
  ReasoningEffort,
  SingleAgentResponse,
} from './types';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

export const AI_AGENTS_QUERY_KEY = ['tenant-ai-config', 'agents'] as const;
export const AI_AVAILABLE_MODELS_QUERY_KEY = ['tenant-ai-config', 'available-models'] as const;
export const AI_FAILOVER_QUERY_KEY = ['tenant-ai-config', 'failover'] as const;
export const AI_PROFILES_QUERY_KEY = ['tenant-ai-config', 'ai-profiles'] as const;
export const AI_DYNAMISM_SCALE_QUERY_KEY = ['tenant-ai-config', 'dynamism-scale'] as const;

export function useAiAgents(enabled = true): UseQueryResult<readonly AgentModelConfigWire[]> {
  return useQuery({
    queryKey: AI_AGENTS_QUERY_KEY,
    queryFn: async () => (await api.listAiAgents()).data,
    enabled,
  });
}

export function useAvailableModels(enabled = true): UseQueryResult<readonly ProviderModelsWire[]> {
  return useQuery({
    queryKey: AI_AVAILABLE_MODELS_QUERY_KEY,
    queryFn: async () => (await api.listAiAvailableModels()).data,
    enabled,
    // Lista do Ollama é "ao vivo" mas raramente muda — 5min de stale é suficiente.
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetAgentModel(): UseMutationResult<
  SingleAgentResponse,
  Error,
  { agentId: string; model: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, model }) => api.setAiAgentModel(agentId, model),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_AGENTS_QUERY_KEY });
    },
  });
}

/** Item 566 — grava o «pensar» do agente (`null` = não escolheu). */
export function useSetAgentReasoning(): UseMutationResult<
  SingleAgentResponse,
  Error,
  { agentId: string; effort: ReasoningEffort | null }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, effort }) => api.setAiAgentReasoning(agentId, effort),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_AGENTS_QUERY_KEY });
    },
  });
}

export function useClearAgentModel(): UseMutationResult<SingleAgentResponse, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (agentId) => api.clearAiAgentModel(agentId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_AGENTS_QUERY_KEY });
    },
  });
}

export function useFailoverEnabled(enabled = true): UseQueryResult<FailoverConfigWire> {
  return useQuery({
    queryKey: AI_FAILOVER_QUERY_KEY,
    // Wire cru `{ enabled }` — sem `.data` (corrige o bug que zerava o toggle no load).
    queryFn: () => api.getFailoverEnabled(),
    enabled,
  });
}

export function useSetFailoverEnabled(): UseMutationResult<FailoverConfigWire, Error, boolean> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabledVal) => api.setFailoverEnabled(enabledVal),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_FAILOVER_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: AI_AGENTS_QUERY_KEY });
    },
  });
}

// Fast-follow — escala de dinamismo do behavioral-agent ───────────────────────

/** Lê a escala atual (corpo cru `{ scale }` — sem envelope data). */
export function useDynamismScale(enabled = true): UseQueryResult<DynamismScaleWire> {
  return useQuery({
    queryKey: AI_DYNAMISM_SCALE_QUERY_KEY,
    queryFn: async () => (await api.getDynamismScale()).scale,
    enabled,
  });
}

export function useSetDynamismScale(): UseMutationResult<
  DynamismScaleResponse,
  Error,
  DynamismScaleWire
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scale) => api.setDynamismScale(scale),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_DYNAMISM_SCALE_QUERY_KEY });
    },
  });
}

export function useSetAgentFallback(): UseMutationResult<
  SetAgentFallbackResponse,
  Error,
  { agentId: string; models: string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, models }) => api.setAgentFallback(agentId, models),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_AGENTS_QUERY_KEY });
    },
  });
}

// Task 6 — perfis de operação de IA ──────────────────────────────────────────

export function useAiProfiles(enabled = true): UseQueryResult<readonly AiProfileWire[]> {
  return useQuery({
    queryKey: AI_PROFILES_QUERY_KEY,
    queryFn: async () => (await api.getAiProfiles()).data,
    enabled,
  });
}

export function useApplyAiProfile(): UseMutationResult<
  ApplyAiProfileResponse,
  Error,
  { profile: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ profile }) => api.applyAiProfile(profile),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_AGENTS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: AI_PROFILES_QUERY_KEY });
    },
  });
}

export function useSetAgentActivationMode(): UseMutationResult<
  { ok: boolean },
  Error,
  { agentId: string; mode: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, mode }) => api.setAgentActivationMode(agentId, mode),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: AI_AGENTS_QUERY_KEY });
    },
  });
}
